import type { UsageReport, UsageTeacher, UsageTotals } from './api';
import { USAGE } from './tokens';

export type SchoolRow = {
  schoolId: string;
  name: string;
  teachersActive: number;
  teachersRostered: number;
  toolboxViews: number;
  joined: number;
  started: number;
  finished: number;
};

export type TeacherRow = {
  userId: string;
  name: string;
  students: number;
  daysLoggedIn: number;
  toolboxViews: number;
  studentsActive: number;
  started: number;
  finished: number;
  tags: string[];
};

function schoolRow(schoolId: string, name: string, totals: UsageTotals): SchoolRow {
  return {
    schoolId, name, teachersActive: totals.teachersActive, teachersRostered: totals.teachersRostered,
    toolboxViews: totals.toolboxViews, joined: totals.academyJoined, started: totals.academyStarted, finished: totals.academyFinished,
  };
}

export function emptyTotals(): UsageTotals {
  return { teachersRostered: 0, teachersActive: 0, studentsRostered: 0, studentsActive: 0, toolboxViews: 0, academyJoined: 0, academyStarted: 0, academyFinished: 0 };
}

export function previousSchoolTotals(previous: UsageReport, schoolId: string): UsageTotals {
  return previous.schools.find((s) => s.schoolId === schoolId)?.totals ?? emptyTotals();
}

export function districtRows(report: UsageReport): { schools: SchoolRow[]; district: SchoolRow } {
  return {
    schools: report.schools.map((school) => schoolRow(school.schoolId, school.name, school.totals)),
    district: schoolRow('', 'District · each teacher once', report.district),
  };
}

export function teacherName(teacher: UsageTeacher): string {
  return `${teacher.firstName} ${teacher.lastName}`.trim();
}

export function teacherTags(teacher: UsageTeacher): string[] {
  const tags: string[] = [];
  if (teacher.studentsRostered < USAGE.smallCaseload) tags.push('Small caseload');
  const everLoggedIn = teacher.daysLoggedIn > 0 || teacher.months.some((m) => m.daysLoggedIn > 0);
  if (!everLoggedIn) tags.push('Not started yet');
  return tags;
}

function byLastName(a: UsageTeacher, b: UsageTeacher): number {
  return a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName) || a.userId.localeCompare(b.userId);
}

export function teachersAt(report: UsageReport, schoolId: string): UsageTeacher[] {
  const ids = new Set(report.schools.find((s) => s.schoolId === schoolId)?.teacherIds ?? []);
  return report.teachers.filter((t) => ids.has(t.userId)).sort(byLastName);
}

export function schoolRows(report: UsageReport, schoolId: string): TeacherRow[] {
  return teachersAt(report, schoolId).map((t) => ({
    userId: t.userId, name: teacherName(t), students: t.studentsRostered, daysLoggedIn: t.daysLoggedIn, toolboxViews: t.toolboxViews,
    studentsActive: t.studentsActive, started: t.academy.coursesStarted, finished: t.academy.coursesFinished, tags: teacherTags(t),
  }));
}

export function loginsByMonth(report: UsageReport, teacherIds: string[], months: string[]): number[] {
  const ids = new Set(teacherIds);
  const teachers = report.teachers.filter((t) => ids.has(t.userId));
  return months.map((month) => teachers.filter((t) => t.months.some((m) => m.month === month && m.daysLoggedIn > 0)).length);
}

export function monthSeries(teacher: UsageTeacher, months: string[], key: 'daysLoggedIn' | 'toolboxViews'): number[] {
  return months.map((month) => teacher.months.find((m) => m.month === month)?.[key] ?? 0);
}

export function delta(current: number, previous: number, previousLabel: string): string {
  if (current === previous) return `No change vs ${previousLabel}`;
  return `${current > previous ? '▲' : '▼'} ${Math.abs(current - previous)} vs ${previousLabel}`;
}

function csvCell(value: string | number): string {
  const raw = String(value);
  const text = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return /[",\n\r\t]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function csvText(columns: string[], rows: (string | number)[][]): string {
  return [columns, ...rows].map((row) => row.map(csvCell).join(',')).join('\n') + '\n';
}

export function downloadCsv(
  name: string,
  text: string,
  create: (blob: Blob) => string = (blob) => URL.createObjectURL(blob),
  revoke: (url: string) => void = (url) => URL.revokeObjectURL(url),
): void {
  const link = document.createElement('a');
  const url = create(new Blob([text], { type: 'text/csv' }));
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => revoke(url), 0);
}
